import getQueryParams from 'src/functions/getQueryParams';
import { getSessionStorage, setSessionStorage } from 'src/functions/localStorageHandler';

const FEATURE_FLAGS = {
  competitive_design: {
    enabled: true,
    description: 'Enables the competitive design website redo experiment',
  },
  disable_analytics: {
    enabled: true,
    description: 'Disables analytics while our analyst is testing Google Tag Manager',
  },
  pro_page_static: {
    enabled: true,
    description: 'Enabled Pro page A/B test',
  },
  path_to_pro_monthly_plan: {
    enabled: true,
    description: 'Updates P2P with monthly pricing',
  },
  pricing_updated_prices: {
    enabled: true,
    description: 'Enables the pricing updated prices experiment',
  },
};

type FeatureFlagKey = keyof typeof FEATURE_FLAGS;

const getDefaultValue = (featureName: FeatureFlagKey): boolean => FEATURE_FLAGS[featureName]?.enabled;

const getFeatureFlagValue = (featureName: FeatureFlagKey): boolean => {
  const sessionStorageValue = getSessionStorage(getSessionStorageKey(featureName));

  if (sessionStorageValue) {
    return sessionStorageValue === 'true';
  }

  return getDefaultValue(featureName);
};

const getSessionStorageKey = (featureName: FeatureFlagKey): string => `@featureFlag:${featureName}`;

const storeSessionOverride = (featureName: FeatureFlagKey, enabled: boolean): void => {
  const sessionStorageKey = getSessionStorageKey(featureName);

  setSessionStorage(sessionStorageKey, enabled.toString());
};

const useFeatureFlag = (featureName: FeatureFlagKey): boolean => {
  // Gets default value for the feature flag on build time
  if (typeof window === 'undefined') return getDefaultValue(featureName);

  const { ff_on: featureFlagOnOverride, ff_off: featureFlagOffOverride } = getQueryParams();

  // Override feature flag value and store it in the local storage
  if (featureFlagOnOverride && decodeURIComponent(featureFlagOnOverride) === featureName) {
    storeSessionOverride(featureName, true);
  }

  if (featureFlagOffOverride && decodeURIComponent(featureFlagOffOverride) === featureName) {
    storeSessionOverride(featureName, false);
  }

  return getFeatureFlagValue(featureName);
};

export default useFeatureFlag;
